import {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./RevenueDetails.module.scss";
import { UnderwritingFormContext } from "../context/UnderwritingFormContext";
import { Field, Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import OutlinedFormInput from "../../Finance/components/OutlinedFormInput/OutlinedFormInput";
import {
  findFirstErrorField,
  onBlurFormatNumber,
  parseNumberForDB,
  setFieldTouchedMutator,
} from "../../../components/functions/utilities";
import moment from "moment";
import { getIn } from "final-form";
import {
  UnderwritingSteps,
  uploadStates,
} from "../../../interfaces/interfaces";
import RevenueRow from "./RevenueRow";
import { MdOutlineStickyNote2 as BannerIcon } from "react-icons/md";
import setFieldTouched from "final-form-set-field-touched";
import RevenueError from "./RevenueError";
import { ARRAY_ERROR } from "final-form";
import API from "../../../rest/api";
import { checkIfGridDataIsValid, revenueFormValidation } from "./revenueHelper";
import DebtSchedule from "./DebtSchedule/DebtSchedule";
import { findFirstDebtScheduleGridError } from "./DebtSchedule/debtScheduleHelper";
import DebtorSchedule from "./DebtorSchedule/DebtorSchedule";
import { findFirstDebtorScheduleGridError } from "./DebtorSchedule/debtorScheduleHelper";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import { HiArrowNarrowDown as DownArrowIcon } from "react-icons/hi";
import { useSearchParams } from "react-router-dom";

export const financialYearLabel = (financialYear) => {
  const financialYears = financialYear.split("-");
  return `${moment(financialYears[0], "YYYY").format("YYYY")}-${moment(
    financialYears[1],
    "YYYY"
  ).format("YY")}`;
};


type Props = {
  setFormPristine: (value: boolean) => void;
  saveToLocalStorage?: (values: Record<string, any>) => void;
};

const RevenueDetails = forwardRef(({ setFormPristine, saveToLocalStorage }: Props, ref) => {
  const {
    formData,
    setFormData,
    setStepValidity,
    formStepsValidity,
    underwritingFileJobs,
    extraFormDetails,
  } = useContext(UnderwritingFormContext);
  // console.log('formData', formData)
  const formRef = useRef(null);
  const [deleteFileKeys, setDeleteFileKeys] = useState([]);
  const [newUploadedDocIds, setNewUploadedDocIds] = useState<number[]>([]);
  const [deleteDocIds, setDeleteDocIds] = useState<number[]>([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const sectionRefs = useRef({
    revenueDetails: createRef(),
    debtSchedule: createRef(),
    debtorSchedule: createRef(),
    unsoldInventory: createRef(),
  });
  const sectionRootContainerRef = useRef(null);
  const [currentVisibleSectionIndex, setCurrentVisibleSectionIndex] =
    useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const showError = searchParams.get("error");
    if (showError == "true") {
      formRef?.current?.submit();
      scrollToFirstError();
    }
  }, [searchParams]);

  const scrollToNextSection = useCallback(() => {
    const sectionKeys = Object.keys(sectionRefs.current);
    // if (sectionKeys.includes('taxes')) {
    //     let index = sectionKeys.indexOf('taxes');

    //     if (index !== -1) {
    //         // Remove the element from its current position
    //         let [element] = sectionKeys.splice(index, 1);

    //         // Add the element to the start of the array
    //         sectionKeys.unshift(element);
    //     }
    // }
    const nextSectionIndex = currentVisibleSectionIndex + 1; // Calculate the next section index
    const nextSectionKey = sectionKeys[nextSectionIndex]; // Get the key for the next section
    if (nextSectionKey) {
      const nextSection = sectionRefs.current[nextSectionKey].current;
      setCurrentVisibleSectionIndex(nextSectionIndex);
      nextSection?.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentVisibleSectionIndex]);

  // Determine the currently visible section based on the top 20% criteria
  const updateVisibleSectionIndex = useCallback(() => {
    const container = sectionRootContainerRef.current;
    const visibleThreshold = container.clientHeight * 0.2; // Top 20% height of the container
    let foundSectionIndex = null;

    const sectionKeys = Object.keys(sectionRefs.current);
    sectionKeys.forEach((key, index) => {
      const section = sectionRefs.current[key].current;
      // console.log('section', section, 'container', container);
      if (section && container) {
        const sectionTop = section.offsetTop - container.scrollTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        // console.log('sectionTop', sectionTop, 'sectionBottom', sectionBottom, 'visibleThreshold', visibleThreshold)
        // If any part of the section is within the top 20% of the container
        if (sectionTop < visibleThreshold && sectionBottom > 0) {
          foundSectionIndex = index;
        }
      }
    });

    // console.log('foundSectionIndex', foundSectionIndex, 'currentVisibleSectionIndex', currentVisibleSectionIndex)

    // Update the state only if a different section is found
    if (
      foundSectionIndex !== null &&
      foundSectionIndex !== currentVisibleSectionIndex
    ) {
      setCurrentVisibleSectionIndex(foundSectionIndex);
    } else {
      if (foundSectionIndex == null) {
        setCurrentVisibleSectionIndex(0);
      }
    }
  }, [currentVisibleSectionIndex]);

  // Listen to scroll events on the container and update the visible section index
  useEffect(() => {
    const container = sectionRootContainerRef.current;
    container.addEventListener("scroll", updateVisibleSectionIndex, {
      passive: true,
    });

    // Initial check in case the initial scroll position is not at the top
    updateVisibleSectionIndex();

    return () => {
      container.removeEventListener("scroll", updateVisibleSectionIndex);
    };
  }, [updateVisibleSectionIndex]);

  const checkScrollButtonVisibility = useCallback(() => {
    const container = sectionRootContainerRef.current;

    const sectionKeys = Object.keys(sectionRefs.current);
    const lastSectionKey = sectionKeys[sectionKeys.length - 1];
    const lastSectionRef = sectionRefs.current[lastSectionKey];
    if (container && lastSectionRef && lastSectionRef.current) {
      const { bottom } = lastSectionRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // console.log('bottom', bottom, 'containerRect', containerRect.bottom)
      // Check if the bottom of the last section is less than or equal to the container's bottom
      const threshold = 10;

      const isLastSectionFullyVisible =
        bottom <= containerRect.bottom + threshold;
      // console.log('isLastSectionFullyVisible', isLastSectionFullyVisible)
      // Hide the scroll button if the last section is fully visible
      setShowScrollButton(!isLastSectionFullyVisible);
    }
  }, []);

  useEffect(() => {
    const container = sectionRootContainerRef.current;

    const handleScroll = () => {
      checkScrollButtonVisibility();
    };

    container.addEventListener("scroll", handleScroll);

    // Initial check
    checkScrollButtonVisibility();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [checkScrollButtonVisibility]);

  useEffect(() => {
    if (sectionRootContainerRef.current) {
      // Scrolling to the top of the container on component mount
      sectionRootContainerRef.current.scrollTop = 0;
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        return new Promise((resolve) => {
          formRef.current.submit();
          // console.log("pristing", formRef?.current)
          console.log("formStepsValidity", formStepsValidity);
          if (!formStepsValidity[UnderwritingSteps.revenue]) {
            scrollToFirstError();
            // console.log('formRef?.current?.getState()', formRef?.current?.getState())
            resolve({
              success: false,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          } else {
            deleteFileKeysOnSubmit();
            resolve({
              success: true,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          }
        });
      },
    }),
    [formStepsValidity]
  );

  const deleteFileKeysOnSubmit = () => {
    if (deleteFileKeys && deleteFileKeys.length) {
      for (const fileKey of deleteFileKeys) {
        try {
          API.post("/app/DeleteDoc", { key: fileKey }).then((res) => res.data);
        } catch (error) {
          console.log("error", error);
        }
      }
    }
  };

  const scrollToFirstError = () => {
    try {
      const formState = formRef.current.getState();
      const { errors } = formState;
      // console.log('form state', formState);
      // console.log('formState', formState)
      const firstErrorField = findFirstErrorField(errors);
      console.log("firstErrorField", firstErrorField);
      const errorField =
        document.getElementById(firstErrorField) ??
        document.querySelector(`[name="${firstErrorField}"]`);
      console.log("errorField", errorField);
      if (errorField) {
        setTimeout(() => {
          errorField.scrollIntoView({ behavior: "smooth" });
        }, 0);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <div className={styles.rootContainer} ref={sectionRootContainerRef}>
      <Form
        initialValues={formData}
        mutators={{
          ...arrayMutators,
          setFieldTouched: setFieldTouchedMutator,
        }}
        onSubmit={(values) => {
          return {};
        }}
        validate={(values) => {
          // console.log('values', values)
          let errors: any = {};
          const revenueDetails = values?.revenueDetails;
          // console.log("revenueDetails", revenueDetails);
          // Revenue column and validation commented out
          // if (revenueDetails && revenueDetails.length) {
          //   let isAllRevenueMissing = true;
          //   let revenueDetailsError = [];
          //   revenueDetails.forEach((revenueDetail, index) => {
          //     if (!revenueDetail?.revenue && revenueDetail?.revenue !== 0) {
          //       if (
          //         revenueDetail?.auditedFinancials?.uploadedFile ||
          //         revenueDetail?.itReturns?.uploadedFile
          //       ) {
          //         isAllRevenueMissing = false;
          //       }
          //     } else {
          //       isAllRevenueMissing = false;
          //     }
          //   });
          //   if (isAllRevenueMissing) {
          //     errors["isRevenueMissing"] = `Enter at least one FY's revenue`;
          //   }
          // }
          const { errors: revenueFormErrors } = revenueFormValidation(
            values,
            underwritingFileJobs
          );
          errors = { ...errors, ...revenueFormErrors };
          console.log("error", errors);
          return errors;
        }}
        render={({ values, handleSubmit, form }) => {
          formRef.current = form;
          return (
            <form className={styles.formWrapper} onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ valid: true, pristine: true, values: true }}
                onChange={(state) => {
                  // console.log('formState', state)
                  setFormPristine(state.pristine);
                  // formStepsValidityRef.current[UnderwritingSteps.company] = !state.valid;
                  setStepValidity(UnderwritingSteps.revenue, state.valid);
                  saveToLocalStorage?.(state.values);
                }}
              />
              <div className={styles.formRootContainer}>
                <div
                  ref={sectionRefs?.current?.revenueDetails as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Financials</div>
                    <div className={styles.sectionSubTitleContainer}>
                      <div className={styles.sectionSubTitle}>
                        Upload Balance Sheet and P&L for each year.
                      {/* Enter your brand’s total revenue and upload available files. */}
                      {/* Add audited financials wherever available */}
                      </div>
                      <div className={styles.infoBanner}>
                        <BannerIcon className={styles.infoBannerIcon} />
                        <span>
                          Remove sheets like Cash Flow, Ratios, etc.
                          before uploading.
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.sectionFieldsContainer}>
                    <FieldArray name="revenueDetails">
                      {({ fields, meta: { error, touched } }) => {
                        return (
                          <>
                            <div className={styles.revenueCardList}>
                              {fields.map((item, index) => {
                                return (
                                  <RevenueRow
                                    key={item}
                                    fieldIndex={index}
                                    fieldName={item}
                                    newUploadedDocIds={newUploadedDocIds}
                                    setNewUploadedDocIds={setNewUploadedDocIds}
                                    deleteDocIds={deleteDocIds}
                                    setDeleteDocIds={setDeleteDocIds}
                                  />
                                );
                              })}
                            </div>
                          </>
                        );
                      }}
                    </FieldArray>
                    {/* <RevenueError /> */}
                  </div>
                </div>

                <div
                  id="debtSchedule"
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.debtSchedule as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Debt Schedule</div>
                  </div>
                  <DebtSchedule />
                </div>

                {/* <div
                  id="debtorSchedule"
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.debtorSchedule as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Debtor Schedule</div>
                  </div>
                  <DebtorSchedule />
                </div>

                <div
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.unsoldInventory as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Inventory Position
                    </div>
                  </div>
                  <div className={styles.filedsContainer}>
                    <Field
                      name="unsoldInventory.underwriting.value"
                      validate={(value) => {
                        if (!value) {
                          return "Enter the value";
                        }
                        return "";
                      }}
                    >
                      {(props) => {
                        return (
                          <div className={styles.fieldContainer}>
                            <OutlinedFormInput
                              {...props.input}
                              textFieldType="outlined"
                              fieldType="lg"
                              showBorder={false}
                              placeholder={"10,00,000"}
                              showPlaceholder={false}
                              inpuType="number"
                              label={`Value of Unsold Inventory as on ${moment()
                                .subtract(1, "month")
                                .endOf("month")
                                .format("MMM DD, YYYY")}`}
                              // containerStyles={{ padding: "0" }}
                              name={props.input.name}
                              value={props.input.value}
                              onChange={(e) => {
                                const numericValue = parseNumberForDB(
                                  e.target.value,
                                  false
                                );
                                console.log("numericValue", numericValue);
                                props.input.onChange(numericValue);
                              }}
                              onBlur={(e) => {
                                const value = props.input.value;
                                const formattedValue = onBlurFormatNumber(
                                  value,
                                  0
                                );
                                props.input.onChange(formattedValue);
                                // console.log('inputValue', value)
                                props.input.onBlur(e);
                              }}
                              unitPlaceHolder="₹"
                              error={props.meta.error}
                              touched={props.meta.touched}
                              isCurrency={true}
                              maxLength={10}
                            />

                            <div className={styles.subText}>
                              Use cost or book value, not MRP.
                            </div>
                          </div>
                        );
                      }}
                    </Field>
                  </div>
                </div> */}
              </div>
            </form>
          );
        }}
      />

      <div className={styles.scrollBtnContainer}>
        {showScrollButton && (
          <MeritoButton
            size="md2"
            onClick={scrollToNextSection}
            isIconToRight={true}
            btnText="Scroll to Next Section"
            type="secondary-1"
            icon={<DownArrowIcon />}
          />
        )}
      </div>
    </div>
  );
});

export default RevenueDetails;
